import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import type { Error } from '../../utils/errorMapper';

const ForgotPasswordValidator = z.object({
  email: z.string().email(),
  recaptcha_response_v3: z.string(),
  recaptcha_response_v2: z.string().optional(),
});

export type ForgotPasswordForm = z.infer<typeof ForgotPasswordValidator>;

export const useForgotPasswordHook = () => {
  const { track } = useAnalytics();

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(ForgotPasswordValidator),
  });

  const [useV2, setUseV2] = useState(false);
  register('recaptcha_response_v3', { required: true });

  const forgot = useMutation<unknown, Error<ForgotPasswordForm>, ForgotPasswordForm>({
    mutationKey: ['accounts/forgot-password'],
    onSuccess: () => {
      track('forgot_password_form_success', {
        event_category: 'Forgot password',
        event_label: 'forgot_password_form',
      });
    },
    onError: (error) => {
      if (error.response?.data?.errors['recaptcha_response_v3']) {
        setUseV2(true);
      }

      track('forgot_password_form_error', {
        event_category: 'Forgot password',
        event_label: 'forgot_password_form',
      });
    },
  });

  const onSubmit = (data: ForgotPasswordForm) => {
    track('forgot_password_form_submit', {
      event_category: 'Forgot password',
      event_label: 'forgot_password_form',
    });

    forgot.mutate(data);
  };

  const onVerify = useCallback((token: string) => {
    setValue('recaptcha_response_v3', token);
  }, []);

  return {
    register,
    errors,
    useV2,
    forgot,
    control,
    handleSubmit,
    onSubmit,
    onVerify,
  };
};
